import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Lock, AlertTriangle, CheckCircle, Sparkles } from 'lucide-react'
import type { AnalysisResult } from '../lib/supabase'

function ResultPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const assessmentId = searchParams.get('id')

  const [result, setResult] = useState<AnalysisResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [checkoutLoading, setCheckoutLoading] = useState(false)

  useEffect(() => {
    if (!assessmentId) {
      navigate('/')
      return
    }

    // Try localStorage first
    const cached = localStorage.getItem(`stresslens_result_${assessmentId}`)
    if (cached) {
      try {
        setResult(JSON.parse(cached))
        setLoading(false)
        return
      } catch { /* fall through */ }
    }

    // If not in localStorage and we have Supabase, we could fetch there
    // For now, redirect home if no cached result
    setError('Assessment not found. Please try again.')
    setLoading(false)
  }, [assessmentId, navigate])

  const handleUnlock = async () => {
    if (!assessmentId) return
    setCheckoutLoading(true)
    try {
      const res = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assessment_id: assessmentId })
      })
      const data = await res.json()
      if (data.url) {
        window.location.href = data.url
      } else {
        throw new Error(data.error || 'Checkout failed')
      }
    } catch (err: any) {
      alert(err.message || 'Could not start checkout. Please try again.')
      setCheckoutLoading(false)
    }
  }

  const stressConfig = {
    mild: { label: 'Mild', color: 'bg-mild/10 text-mild border-mild/30', icon: CheckCircle },
    moderate: { label: 'Moderate', color: 'bg-moderate/10 text-moderate border-moderate/30', icon: AlertTriangle },
    severe: { label: 'Severe', color: 'bg-severe/10 text-severe border-severe/30', icon: AlertTriangle }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="w-2.5 h-2.5 rounded-full bg-primary/60 animate-pulse-dot" />
            <span className="w-2.5 h-2.5 rounded-full bg-primary/60 animate-pulse-dot-delay-1" />
            <span className="w-2.5 h-2.5 rounded-full bg-primary/60 animate-pulse-dot-delay-2" />
          </div>
          <p className="text-text-light text-sm animate-pulse">Analyzing your responses...</p>
        </div>
      </div>
    )
  }

  if (error || !result) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg px-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-10 h-10 text-severe mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-text mb-2">Something went wrong</h2>
          <p className="text-text-light text-sm mb-6">{error || 'Assessment not found.'}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-primary text-white px-6 py-2.5 rounded-xl font-medium hover:bg-primary-dark transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    )
  }

  const stress = stressConfig[result.stress_level]
  const StressIcon = stress.icon

  return (
    <div className="min-h-screen bg-bg">
      {/* Header */}
      <nav className="w-full bg-bg/80 backdrop-blur-sm border-b border-border sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-1.5 text-sm text-text-light hover:text-text transition-colors">
            <ArrowLeft className="w-4 h-4" /> Home
          </button>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-primary flex items-center justify-center">
              <Sparkles className="w-3 h-3 text-white" />
            </div>
            <span className="font-semibold text-text text-sm">StressLens</span>
          </div>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-4 py-8 pb-24">
        {/* Crisis Banner */}
        {result.risk_level === 'high' && (
          <div className="bg-severe/10 border border-severe/30 rounded-xl p-4 mb-6 animate-fade-in-up">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-severe shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-severe text-sm mb-1">We're concerned about you</h3>
                <p className="text-sm text-text-light mb-3">Your responses suggest you may be going through a very difficult time. Please reach out for support right now.</p>
                <div className="space-y-1.5">
                  {result.seek_help.crisis_resources.map((r, i) => (
                    <div key={i} className="text-sm font-medium text-severe">{r}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stress Level Badge */}
        <div className="text-center mb-8 animate-fade-in-up">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold ${stress.color}`}>
            <StressIcon className="w-4 h-4" />
            Your stress level: {stress.label}
          </div>
          <p className="mt-3 text-sm text-text-light">
            {result.stress_level === 'mild' && "Your responses suggest mild stress. You're experiencing normal life pressures."}
            {result.stress_level === 'moderate' && "Your responses suggest moderate stress. There are patterns worth addressing."}
            {result.stress_level === 'severe' && "Your responses suggest significant stress. These patterns are affecting multiple areas of your life."}
          </p>
        </div>

        {/* Summary */}
        <section className="bg-white rounded-2xl border border-border p-5 sm:p-6 mb-6 animate-fade-in-up">
          <h2 className="text-sm font-semibold text-text-light uppercase tracking-wider mb-3">Summary</h2>
          <p className="text-text leading-relaxed text-base">{result.summary}</p>
        </section>

        {/* Cognitive Patterns */}
        <section className="bg-white rounded-2xl border border-border p-5 sm:p-6 mb-6 animate-fade-in-up">
          <h2 className="text-sm font-semibold text-text-light uppercase tracking-wider mb-4">Cognitive Patterns</h2>
          <div className="space-y-5">
            {result.cognitive_patterns.map((pattern, i) => (
              <div key={i} className="border-l-2 border-primary/30 pl-4">
                <div className="font-medium text-text mb-1">{pattern.distortion}</div>
                <div className="text-sm text-text-light mb-2 italic">"{pattern.evidence}"</div>
                <div className="text-sm text-primary font-medium">{pattern.reframe}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Triggers */}
        <section className="bg-white rounded-2xl border border-border p-5 sm:p-6 mb-6 animate-fade-in-up">
          <h2 className="text-sm font-semibold text-text-light uppercase tracking-wider mb-3">Identified Triggers</h2>
          <div className="flex flex-wrap gap-2">
            {result.triggers.map((trigger, i) => (
              <span key={i} className="px-3 py-1.5 rounded-lg bg-bg border border-border text-sm text-text">
                {trigger}
              </span>
            ))}
          </div>
        </section>

        {/* Immediate Actions */}
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-text-light uppercase tracking-wider mb-4 px-1">Immediate Actions</h2>
          <div className="space-y-4">
            {result.immediate_actions.map((action, i) => {
              const isLocked = i > 0
              return (
                <div
                  key={i}
                  className={`bg-white rounded-2xl border border-border p-5 sm:p-6 relative ${isLocked ? 'overflow-hidden' : ''}`}
                >
                  {isLocked && (
                    <>
                      <div className="absolute inset-0 bg-bg/60 backdrop-blur-[2px] z-10 flex flex-col items-center justify-center">
                        <Lock className="w-6 h-6 text-text-light mb-2" />
                        <span className="text-sm font-medium text-text-light">Locked</span>
                      </div>
                      <div className="blur-[3px] select-none">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-text">{action.title}</h3>
                          <span className="text-xs text-text-light bg-bg px-2 py-1 rounded-md">{action.duration}</span>
                        </div>
                        <p className="text-sm text-text-light mb-3">{action.instructions}</p>
                        <p className="text-xs text-primary">{action.why_it_helps}</p>
                      </div>
                    </>
                  )}
                  {!isLocked && (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-text">{action.title}</h3>
                        <span className="text-xs text-text-light bg-bg px-2 py-1 rounded-md">{action.duration}</span>
                      </div>
                      <p className="text-sm text-text-light mb-3 leading-relaxed">{action.instructions}</p>
                      <p className="text-xs text-primary font-medium">{action.why_it_helps}</p>
                    </>
                  )}
                </div>
              )
            })}
          </div>
        </section>

        {/* 7-Day Plan */}
        <section className="mb-6">
          <h2 className="text-sm font-semibold text-text-light uppercase tracking-wider mb-4 px-1">Your 7-Day Plan</h2>
          <div className="space-y-4">
            {result.seven_day_plan.map((day, i) => {
              const isLocked = i > 1
              return (
                <div
                  key={i}
                  className={`bg-white rounded-2xl border border-border p-5 sm:p-6 relative ${isLocked ? 'overflow-hidden' : ''}`}
                >
                  {isLocked && (
                    <>
                      <div className="absolute inset-0 bg-bg/60 backdrop-blur-[2px] z-10 flex flex-col items-center justify-center">
                        <Lock className="w-6 h-6 text-text-light mb-2" />
                        <span className="text-sm font-medium text-text-light">Locked</span>
                      </div>
                      <div className="blur-[3px] select-none">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="w-8 h-8 rounded-lg bg-bg border border-border flex items-center justify-center text-xs font-bold text-text-light">
                            {day.day}
                          </span>
                          <div>
                            <h3 className="font-semibold text-text text-sm">{day.focus}</h3>
                            <span className="text-xs text-text-light">{day.duration}</span>
                          </div>
                        </div>
                        <p className="text-sm text-text-light mb-2">{day.action}</p>
                        <p className="text-xs text-text-light italic">{day.reflection_prompt}</p>
                      </div>
                    </>
                  )}
                  {!isLocked && (
                    <>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-xs font-bold text-primary">
                          {day.day}
                        </span>
                        <div>
                          <h3 className="font-semibold text-text text-sm">{day.focus}</h3>
                          <span className="text-xs text-text-light">{day.duration}</span>
                        </div>
                      </div>
                      <p className="text-sm text-text-light mb-2 leading-relaxed">{day.action}</p>
                      <p className="text-xs text-text-light italic">💭 {day.reflection_prompt}</p>
                    </>
                  )}
                </div>
              )
            })}
          </div>
        </section>

        {/* Seek Help */}
        <section className="bg-white rounded-2xl border border-border p-5 sm:p-6 mb-8">
          <h2 className="text-sm font-semibold text-text-light uppercase tracking-wider mb-3">When to Seek Help</h2>
          <p className="text-sm text-text-light leading-relaxed mb-4">{result.seek_help.reason}</p>
          {result.seek_help.crisis_resources.length > 0 && (
            <div className="space-y-2">
              {result.seek_help.crisis_resources.map((r, i) => (
                <div key={i} className="text-sm font-medium text-severe bg-severe/5 px-3 py-2 rounded-lg">{r}</div>
              ))}
            </div>
          )}
        </section>

        {/* Paywall CTA */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-border p-4 z-40">
          <div className="max-w-2xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-center sm:text-left">
              <div className="text-sm font-semibold text-text">Unlock Your Full Report</div>
              <div className="text-xs text-text-light">Complete analysis · All 3 actions · Full 7-day plan</div>
            </div>
            <button
              onClick={handleUnlock}
              disabled={checkoutLoading}
              className="w-full sm:w-auto bg-accent hover:bg-accent-dark disabled:opacity-60 text-white font-medium px-8 py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              {checkoutLoading ? 'Loading...' : 'Unlock for $3.99'}
            </button>
          </div>
        </div>
      </main>
    </div>
  )
}

export default ResultPage
